import { AsyncPipe, NgClass, NgFor, NgIf, SlicePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule, RouterState } from '@angular/router';
import { Store } from '@ngrx/store';
import { ModuleTypes } from '@shared/models/modules.enum';
import { ImageResizePipe } from '@shared/pipes/image-resize.pipe';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import * as routerStore from '@shared/store';
import { TourAnchorMatMenuDirective } from 'ngx-ui-tour-md-menu';
import { Observable, Subscription } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { defaultHeartSvg } from '../assets/heart_v2.svg';
import { defaultStarSvg } from '../assets/star.svg';
import { CardBodyComponent } from '../components/card-body/card-body.component';

import { EsgComponent } from '@shared/shared/components/card-esg/esg.component';
import { CardBottomComponent } from '../components/card-bottom/card-bottom.component';
import { CardLikeComponent } from '../components/card-like/card-like.component';
import { CardRibbonComponent } from '../components/card-ribbon/card-ribbon.component';
import { FollowButtonComponent } from '../components/follow-button/follow-button.component';
import { Card, FollowButton } from '../models/card';
import { CardSvgStoreService } from '../services/card-svg-store.service';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';

@Directive({
  selector: 'ten-card-value-proposition',
  standalone: true,
})
export class CardValuePropositionDirective {}

@Component({
  selector: 'ten-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
  standalone: true,
  imports: [
    RouterModule,
    NgClass,
    NgIf,
    NgFor,
    CardRibbonComponent,
    MatIconModule,
    CardLikeComponent,
    FollowButtonComponent,
    AsyncPipe,
    TourAnchorMatMenuDirective,
    CardBodyComponent,
    ImageResizePipe,
    SlicePipe,
    EsgComponent,
    CardBottomComponent,
    AccessibleClickDirective,
  ],
})
export class CardComponent implements OnDestroy, AfterViewInit {
  @Input() public currency: string;
  @Input() public tagModule: string;
  @Input() public badgesOnTop = false;
  @Input() public enableAnchorLink = false;
  @Input() public asyncPageLoad = false;
  @Input() public openNewTab = false;
  @Input() public showCardBottom = false;

  @Output() public cardLiked = new EventEmitter<boolean>();
  @Output() public cardClicked = new EventEmitter<Event>();
  @Output() public openModalParent = new EventEmitter<void>();
  @Output() public preferenceChange = new EventEmitter<boolean>();

  @ViewChild('cardLink', { static: true }) public cardLinkElement: ElementRef<HTMLAnchorElement>;

  public imageUrl: string;
  public isShowMoreInfo = false;
  public isFollowButtonEnabled$: Observable<boolean>;
  public isDefaultCarImage: boolean;
  public followButtonData: FollowButton;
  public displaySupplierName: boolean;

  private _subscriptions: Array<Subscription> = [];
  public get subscriptions(): Array<Subscription> {
    return this._subscriptions;
  }

  private _likeSvg: string = defaultHeartSvg;
  public get likeSvg(): string {
    return this._likeSvg;
  }
  @Input() public set likeSvgUrl(url: string) {
    this.subscriptions.push(
      this.svgStore.fetchSvg(url).subscribe((res) => {
        this._likeSvg = res;
      })
    );
  }

  private _starSvg: string = defaultStarSvg;
  public get starSvg(): string {
    return this._starSvg;
  }
  @Input() public set starSvgUrl(url: string) {
    this.subscriptions.push(
      this.svgStore.fetchSvg(url).subscribe((res) => {
        this._starSvg = res;
      })
    );
  }

  private _cardData: Card;
  public get cardData(): Card {
    return this._cardData;
  }
  @Input() public set cardData(value: Card) {
    this._cardData = value;

    this.followButtonData = this.cardData.followButton;

    if (value?.imageUrl?.includes('.') || value?.image_url?.includes('.')) {
      this.imageUrl = value.imageUrl || value.image_url;
    }
  }

  @Input() public set isFollowed(value: boolean) {
    if (value !== undefined) {
      this.followButtonData = { ...this.cardData.followButton, ...{ preferred: value } };
    }
  }

  constructor(
    private ngrxStore: Store<RouterState>,
    private svgStore: CardSvgStoreService,
    private siteSettingsService: SiteSettingsService
  ) {
    this.isFollowButtonEnabled$ = this.showFollowButton();
  }

  public ngAfterViewInit() {
    if (!this.enableAnchorLink || !this.cardData.url) {
      this.cardLinkElement.nativeElement.removeAttribute('href');
    }
  }

  public applyBackUpImage() {
    this.imageUrl = this.cardData.backupImageUrl;
    this.isDefaultCarImage = /Travel_Cars_Default.jpg/.test(this.imageUrl);
  }

  public setIsShowMoreInfo(event: boolean) {
    this.isShowMoreInfo = event;
  }

  public showFollowButton(): Observable<boolean> {
    return this.ngrxStore.select(routerStore.getCurrentModule).pipe(
      withLatestFrom(this.siteSettingsService.fetchSiteSettings()),
      map(
        ([currentModule, settings]) =>
          (currentModule === ModuleTypes.Entertainment &&
            !settings.switches.entertainment_redesign_phase_1_1 &&
            (settings.switches.preferences_artist || settings.switches.preferences_interests)) ||
          (currentModule === ModuleTypes.Travel && settings.switches.preferences_travel) ||
          (currentModule === ModuleTypes.Dining && settings.switches.preferences_dining) ||
          (currentModule === ModuleTypes.MemberBenefits && settings.switches.preferences_member_benefits) ||
          (currentModule === ModuleTypes.MemberEvents &&
            settings.switches.preferences_interests &&
            settings.switches.preferences_follow_events_and_entertainment)
      )
    );
  }

  public openModal() {
    this.openModalParent.next();
  }

  public onCardLiked(liked: boolean) {
    this.cardLiked.emit(liked);
  }

  public onCardClicked(event: Event) {
    if ((event.target as HTMLElement).closest('.follow-button')) {
      return;
    }
    if (!this.enableAnchorLink || !this.cardData.url) {
      event.preventDefault();
    }

    if (event.type !== 'auxclick' || (event.type === 'auxclick' && this.cardData.type === 'car')) {
      this.cardClicked.emit(event);
    }
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
